import {
  ActionIcon,
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuDownload, LuTrash2 } from 'react-icons/lu'
import { CurrencyText } from '@/frontend/components/marina/CurrencyText'
import { MoneyText } from '@/frontend/components/marina/MoneyText'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import {
  useCancelInvoice,
  useCreatePayment,
  useDeletePayment,
  useFinalizeInvoice,
  useInvoice,
  useSendInvoice,
} from '@/frontend/hooks/useInvoices'
import { humanizeError } from '@/frontend/lib/errors'

const PAYMENT_METHODS = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'CHECK', label: 'Check' },
  { value: 'OTHER', label: 'Other' },
]

export const Route = createFileRoute('/dashboard/billing/invoices/$id')({
  component: InvoiceDetail,
})

function InvoiceDetail() {
  const { id } = Route.useParams()
  const { t } = useTranslation()
  const { data, isLoading } = useInvoice(id)
  const finalize = useFinalizeInvoice()
  const send = useSendInvoice()
  const cancel = useCancelInvoice()
  const createPay = useCreatePayment()
  const delPay = useDeletePayment()

  const [payModal, payModalH] = useDisclosure(false)

  if (isLoading)
    return (
      <Container size="xl" py="md">
        <Text c="dimmed">{t('page.billing.invoices.detail.loading')}</Text>
      </Container>
    )
  if (!data)
    return (
      <Container size="xl" py="md">
        <Text c="dimmed">{t('page.billing.invoices.detail.not_found')}</Text>
      </Container>
    )
  const inv = data.invoice
  const customerName = inv.customer ? `${inv.customer.firstName} ${inv.customer.lastName}`.trim() : '—'
  const balance = Number(inv.totalIdr) - Number(inv.paidIdr)

  const notify = async (p: Promise<unknown>, success: string) => {
    try {
      await p
      notifications.show({ title: t('common.updated'), message: success, color: 'teal' })
    } catch (e: unknown) {
      notifications.show({ title: t('common.failed'), message: humanizeError(e, t), color: 'red' })
    }
  }

  const confirm = (
    title: string,
    body: string,
    confirmLabel: string,
    fn: () => Promise<void>,
    color: 'teal' | 'red' = 'teal',
  ) =>
    modals.openConfirmModal({
      title,
      children: <Text size="sm">{body}</Text>,
      labels: { confirm: confirmLabel, cancel: t('common.cancel') },
      confirmProps: { color },
      onConfirm: fn,
    })

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor component={Link} to="/dashboard/billing/invoices" c="dimmed">
            {t('page.billing.invoices.title')}
          </Anchor>
          <span>{inv.code}</span>
        </Breadcrumbs>

        <Card padding="lg" radius="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="xs" mb={4}>
                <Title order={3}>{inv.code}</Title>
                <StatusBadge status={inv.status} kind="invoice" />
              </Group>
              <Text c="dimmed" size="sm">
                {customerName}
                {inv.boat?.name && <> · {inv.boat.name}</>}
                {inv.booking?.code && <> · Booking {inv.booking.code}</>}
              </Text>
              <Text c="dimmed" size="sm">
                Issued {dayjs(inv.issueDate).format('DD MMM YYYY')} · Due {dayjs(inv.dueDate).format('DD MMM YYYY')}
              </Text>
              {inv.quote && (
                <Badge variant="light" mt="xs" component="a" href={`/dashboard/billing/quotes/${inv.quote.id}`}>
                  {t('page.billing.invoices.detail.linked_quote')} {inv.quote.code}
                </Badge>
              )}
            </div>
            <Group gap="xs">
              <Button
                variant="default"
                leftSection={<LuDownload size={16} />}
                component="a"
                href={`/api/invoices/${inv.id}/pdf`}
                target="_blank"
              >
                PDF
              </Button>
              {inv.status === 'PRO_FORMA' && (
                <Button
                  color="laut"
                  onClick={() =>
                    confirm(
                      t('page.billing.invoices.detail.action_finalize_title'),
                      t('page.billing.invoices.detail.action_finalize_body'),
                      t('page.billing.invoices.detail.action_finalize'),
                      () => notify(finalize.mutateAsync(inv.id), t('page.billing.invoices.detail.action_finalize')),
                    )
                  }
                >
                  {t('page.billing.invoices.detail.action_finalize')}
                </Button>
              )}
              {inv.status === 'DRAFT' && (
                <Button
                  color="laut"
                  onClick={() =>
                    confirm(
                      t('page.billing.invoices.detail.action_send_title'),
                      t('page.billing.invoices.detail.action_send_body'),
                      t('page.billing.invoices.detail.action_send'),
                      () => notify(send.mutateAsync(inv.id), t('page.billing.invoices.detail.action_send')),
                    )
                  }
                >
                  {t('page.billing.invoices.detail.action_send')}
                </Button>
              )}
              {['SENT', 'PARTIALLY_PAID', 'OVERDUE'].includes(inv.status) && (
                <Button color="karang" onClick={payModalH.open}>
                  {t('page.billing.invoices.detail.action_record_payment')}
                </Button>
              )}
              {!['PAID', 'CANCELLED'].includes(inv.status) && (
                <Button
                  variant="light"
                  color="red"
                  onClick={() =>
                    confirm(
                      t('page.billing.invoices.detail.action_void_title'),
                      t('page.billing.invoices.detail.action_void_body'),
                      t('page.billing.invoices.detail.action_void'),
                      () => notify(cancel.mutateAsync(inv.id), t('page.billing.invoices.detail.action_void')),
                      'red',
                    )
                  }
                >
                  {t('page.billing.invoices.detail.action_void')}
                </Button>
              )}
            </Group>
          </Group>
        </Card>

        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Card padding="lg" radius="md">
              <Text fw={600} mb="sm">
                Line Items
              </Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Description</Table.Th>
                    <Table.Th ta="right">Qty</Table.Th>
                    <Table.Th ta="right">Unit Price</Table.Th>
                    <Table.Th ta="right">Amount</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(inv.lineItems ?? []).map((it) => (
                    <Table.Tr key={it.id}>
                      <Table.Td>{it.description}</Table.Td>
                      <Table.Td ta="right">{Number(it.quantity)}</Table.Td>
                      <Table.Td ta="right">
                        <CurrencyText idr={it.unitPriceIdr} />
                      </Table.Td>
                      <Table.Td ta="right">
                        <CurrencyText idr={it.amountIdr} fw={500} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              <Divider my="md" />
              <Stack gap={4} align="flex-end">
                <Group gap="md">
                  <Text size="sm" c="dimmed">
                    Subtotal
                  </Text>
                  <CurrencyText idr={inv.subtotalIdr} miw={140} ta="right" />
                </Group>
                <Group gap="md">
                  <Text size="sm" c="dimmed">
                    Tax ({Number(inv.taxRate)}%)
                  </Text>
                  <CurrencyText idr={inv.taxIdr} miw={140} ta="right" />
                </Group>
                <Group gap="md">
                  <Text fw={700} size="sm">
                    Total
                  </Text>
                  <MoneyText
                    amount={inv.totalIdr}
                    currency={inv.currency}
                    fw={700}
                    size="lg"
                    c="laut.7"
                    miw={140}
                    ta="right"
                  />
                </Group>
                <Group gap="md">
                  <Text size="sm" c="dimmed">
                    Paid
                  </Text>
                  <CurrencyText idr={inv.paidIdr} miw={140} ta="right" c="green.7" />
                </Group>
                <Group gap="md">
                  <Text size="sm" c="dimmed">
                    Balance
                  </Text>
                  <CurrencyText idr={balance} miw={140} ta="right" fw={600} c={balance > 0 ? 'red.7' : undefined} />
                </Group>
              </Stack>
            </Card>

            <Card padding="lg" radius="md" mt="md">
              <Group justify="space-between" mb="sm">
                <Text fw={600}>Payments</Text>
              </Group>
              {inv.payments && inv.payments.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Method</Table.Th>
                      <Table.Th>Reference</Table.Th>
                      <Table.Th ta="right">Amount</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {inv.payments.map((p) => (
                      <Table.Tr key={p.id}>
                        <Table.Td>{dayjs(p.paidAt).format('DD MMM YYYY')}</Table.Td>
                        <Table.Td>
                          <Text size="sm">{p.method.replace('_', ' ')}</Text>
                        </Table.Td>
                        <Table.Td>{p.reference ?? '—'}</Table.Td>
                        <Table.Td ta="right">
                          <CurrencyText idr={p.amountIdr} fw={500} />
                        </Table.Td>
                        <Table.Td ta="right">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label="Delete payment"
                            onClick={() =>
                              modals.openConfirmModal({
                                title: 'Delete Payment',
                                children: <Text size="sm">Hapus payment ini? Invoice balance akan disesuaikan.</Text>,
                                labels: { confirm: 'Delete', cancel: 'Cancel' },
                                confirmProps: { color: 'red' },
                                onConfirm: () =>
                                  notify(delPay.mutateAsync({ invoiceId: inv.id, payId: p.id }), 'Payment deleted'),
                              })
                            }
                          >
                            <LuTrash2 size={14} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text c="dimmed" size="sm">
                  No payments recorded.
                </Text>
              )}
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card padding="lg" radius="md">
              <Text fw={600} mb="sm">
                Billing Recipient
              </Text>
              <Stack gap={4}>
                <Info label="Name" value={inv.recipientName ?? customerName} />
                <Info label="Email" value={inv.recipientEmail ?? inv.customer?.email} />
                <Info label="Company" value={inv.recipientCompanyName} />
                <Info label="Tax ID" value={inv.recipientTaxId} />
                <Info label="Address" value={inv.recipientAddress} />
              </Stack>
            </Card>
            {inv.notes && (
              <Card padding="lg" radius="md" mt="md">
                <Text fw={600} mb="sm">
                  Notes
                </Text>
                <Text size="sm">{inv.notes}</Text>
              </Card>
            )}
          </Grid.Col>
        </Grid>
      </Stack>

      <PaymentModal
        opened={payModal}
        onClose={payModalH.close}
        maxAmount={balance}
        onSubmit={async ({ amountIdr, method, paidAt, reference, notes }) => {
          try {
            await createPay.mutateAsync({
              invoiceId: inv.id,
              data: { amountIdr, method, paidAt, reference: reference || null, notes: notes || null },
            })
            notifications.show({
              title: 'Payment recorded',
              message: `Rp${amountIdr.toLocaleString('id-ID')}`,
              color: 'teal',
            })
            payModalH.close()
          } catch (e: unknown) {
            notifications.show({
              title: 'Failed',
              message: e instanceof Error ? e.message : 'Try again',
              color: 'red',
            })
          }
        }}
      />
    </Container>
  )
}

function PaymentModal({
  opened,
  onClose,
  maxAmount,
  onSubmit,
}: {
  opened: boolean
  onClose: () => void
  maxAmount: number
  onSubmit: (data: { amountIdr: number; method: any; paidAt?: string; reference?: string; notes?: string }) => void
}) {
  const { t } = useTranslation()
  const [amount, setAmount] = useState(maxAmount)
  const [method, setMethod] = useState<string>('BANK_TRANSFER')
  const [paidAt, setPaidAt] = useState(dayjs().format('YYYY-MM-DD'))
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')

  return (
    <Modal opened={opened} onClose={onClose} title={t('page.billing.invoices.detail.payment_modal_title')}>
      <Stack gap="md">
        <NumberInput
          label={t('page.billing.invoices.detail.payment_modal_amount')}
          required
          value={amount}
          onChange={(v) => setAmount(typeof v === 'number' ? v : 0)}
          min={1}
          step={100_000}
          thousandSeparator=","
          description={`${t('page.billing.invoices.detail.remaining_balance', { defaultValue: 'Remaining balance' })}: Rp${maxAmount.toLocaleString('id-ID')}`}
        />
        <Select
          label={t('page.billing.invoices.detail.payment_modal_method')}
          data={PAYMENT_METHODS}
          value={method}
          onChange={(v) => setMethod(v ?? 'BANK_TRANSFER')}
        />
        <TextInput
          label={t('page.billing.invoices.detail.payment_modal_paid_at', { defaultValue: 'Paid at' })}
          type="date"
          value={paidAt}
          onChange={(e) => setPaidAt(e.currentTarget.value)}
        />
        <TextInput
          label={t('page.billing.invoices.detail.payment_modal_reference')}
          placeholder="BCA 1234567890"
          value={reference}
          onChange={(e) => setReference(e.currentTarget.value)}
        />
        <Textarea
          label={t('page.billing.invoices.detail.payment_modal_notes', { defaultValue: 'Notes' })}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button color="laut" onClick={() => onSubmit({ amountIdr: amount, method, paidAt, reference, notes })}>
            {t('page.billing.invoices.detail.payment_modal_submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" ta="right" style={{ maxWidth: '70%' }}>
        {value || '—'}
      </Text>
    </Group>
  )
}
